import React from "react";

interface ProgressBarProps {
  percentage: number;
  className?: string;
}

/**
 * 3-tier cleanliness progress bar (Sweepy-style).
 * `percentage` = dirty percentage (0 = just cleaned, 100 = overdue).
 * The bar fills as the item gets dirtier.
 *
 * Tiers:
 *  - Green  (0–60% dirty): "Shiny clean"
 *  - Yellow (60–90% dirty): "Needs attention"
 *  - Red    (90%+ dirty):   "Time for action"
 */
export const ProgressBar: React.FC<ProgressBarProps> = ({
  percentage,
  className = "",
}) => {
  const dirty = Math.max(0, Math.min(100, percentage));

  const tier = dirty >= 90 ? "bad" : dirty >= 60 ? "warn" : "good";

  const fill = {
    good: "bg-emerald-500 dark:bg-emerald-400",
    warn: "bg-amber-400",
    bad: "bg-red-500 dark:bg-red-400",
  }[tier];

  const track = {
    good: "bg-emerald-500/15",
    warn: "bg-amber-400/20",
    bad: "bg-red-500/15",
  }[tier];

  const message = {
    good: "Shiny clean",
    warn: "Needs attention",
    bad: "Time for action",
  }[tier];

  const textColor = {
    good: "text-emerald-700 dark:text-emerald-300",
    warn: "text-amber-700 dark:text-amber-300",
    bad: "text-red-600 dark:text-red-300",
  }[tier];

  return (
    <div className={className}>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(dirty)}
        aria-valuetext={`${Math.round(dirty)}% — ${message}`}
        className={`w-full h-2.5 ${track} rounded-full overflow-hidden`}
      >
        <div
          className={`h-full ${fill} transition-all duration-500 ease-out rounded-full`}
          style={{ width: `${dirty}%` }}
        />
      </div>
      <div className={`text-[13px] font-semibold ${textColor} mt-1.5`}>
        {message}
      </div>
    </div>
  );
};

export default ProgressBar;
